/**
 * Phase 3.43 — Unternehmensdaten-Block, eingebettet am Ende der Stammdaten.
 *
 * Zeigt die fünf Sub-Akkordeons: Konto-Zugang, Gewerbe-Nachweise,
 * Steuerdaten, Impressum, Rechtliches.
 */
import { useState } from "react";
import { Lock, FileText, Receipt, FileSignature, ScrollText, Building2, Calculator, Landmark, CreditCard } from "lucide-react";
import {
    AccountSection,
    BusinessDocSection,
    SteuerdatenSection,
    ImpressumSection,
    TwoFactorSection,
    BankSection,
} from "../DealerSettings";
import { DatevStammdatenPanel } from "./FinanzenSection";
import { StripeConnectCard } from "./StripeConnectCard";
import LegalDocsUploader from "./LegalDocsUploader";
import DealerLegalDocs from "./DealerLegalDocs";
import OrgLockedSection from "./OrgLockedSection";
import BranchOverrideToggle from "./BranchOverrideToggle";

export function UnternehmensdatenBlock({ currentUser }) {
    const [open, setOpen] = useState({ account: false, docs: false, steuer: false, datev: false, bank: false, stripeconnect: false, imprint: false, legal: false });
    const toggle = (k) => setOpen((p) => ({ ...p, [k]: !p[k] }));
    const Header = ({ id, icon: Icon, title, hint, right }) => (
        <div className="flex items-center bg-gray-50 hover:bg-gray-100 border-b border-gray-200">
            <button
                type="button"
                onClick={() => toggle(id)}
                className="flex-1 flex items-center gap-3 px-5 py-3 text-left min-w-0"
                data-testid={`stammdaten-unternehmen-toggle-${id}`}
            >
                <Icon className="w-4 h-4 text-[#FFCC00] shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-[#111827]">{title}</div>
                    <div className="text-[11px] text-gray-500">{hint}</div>
                </div>
                <span className={`text-gray-400 text-lg transition-transform ${open[id] ? "rotate-180" : ""}`}>⌄</span>
            </button>
            {right && <div className="pr-4 pl-2 shrink-0">{right}</div>}
        </div>
    );
    return (
        <div className="mt-8 border-2 border-gray-200 rounded-sm overflow-hidden" data-testid="stammdaten-unternehmensdaten-block">
            <div className="px-5 py-3 bg-gray-100 border-b-2 border-gray-200 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-[#FFCC00]" />
                <h3 className="font-display font-bold text-sm uppercase tracking-wider text-[#111827]">Unternehmensdaten</h3>
            </div>
            <Header id="account" icon={Lock} title="Konto-Zugang" hint="E-Mail- und Passwort-Änderung · Zwei-Faktor-Authentifizierung (2FA)" />
            {open.account && (
                <div className="p-5 border-b border-gray-200 space-y-6" data-testid="stammdaten-unternehmen-account-body">
                    <AccountSection currentUser={currentUser} embedded />
                    {/* Jun-2026 (User-Wunsch) — 2FA hierher (Konto-Zugang) verschoben. */}
                    <div className="pt-6 border-t border-gray-100" data-testid="stammdaten-unternehmen-2fa">
                        <TwoFactorSection currentUser={currentUser} />
                    </div>
                </div>
            )}
            <Header id="docs" icon={FileText} title="Gewerbe-Nachweise" hint="Gewerbeanmeldung / Handelsregisterauszug + Verifizierung"
                right={<BranchOverrideToggle area="business_docs" currentUser={currentUser}
                    infoText="Gewerbe-Nachweise (Gewerbeanmeldung / Handelsregisterauszug) des Hauptsitzes für alle Filialen übernehmen."
                    testId="business-docs-branch-override" />}
            />
            {open.docs && (
                <div className="p-5 border-b border-gray-200" data-testid="stammdaten-unternehmen-docs-body">
                    <BusinessDocSection currentUser={currentUser} embedded />
                </div>
            )}
            <Header id="steuer" icon={Receipt} title="Steuerdaten" hint="Steuer-ID und Umsatzsteuer-Identifikationsnummer"
                right={<BranchOverrideToggle area="tax" currentUser={currentUser}
                    infoText="Steuerdaten & Rechtsperson (Steuer-ID, USt-IdNr., Handelsregister, Geschäftsführer) des Hauptsitzes für alle Filialen übernehmen."
                    testId="tax-branch-override" />}
            />
            {open.steuer && (
                <div className="p-5 border-b border-gray-200" data-testid="stammdaten-unternehmen-steuer-body">
                    <SteuerdatenSection currentUser={currentUser} embedded />
                </div>
            )}
            {/* DATEV-Stammdaten (Jun-2026, User-Wunsch) — aus „Vertragsverhältnis" hierher verschoben. */}
            <Header id="datev" icon={Calculator} title="Berater-/Mandantennummer & Wirtschaftsjahr" hint="Für den Steuerberater-Export (DATEV)"
                right={<BranchOverrideToggle area="datev" currentUser={currentUser}
                    infoText="DATEV-Stammdaten (Berater-/Mandantennummer, Wirtschaftsjahr, Kontenrahmen) des Hauptsitzes für alle Filialen übernehmen."
                    testId="datev-branch-override" />}
            />
            {open.datev && (
                <div className="p-5 border-b border-gray-200" data-testid="stammdaten-unternehmen-datev-body">
                    <DatevStammdatenPanel currentUser={currentUser} />
                </div>
            )}
            {/* Bankverbindung + Stripe (Jun-2026, User-Wunsch) — aus „Vertragsverhältnis" hierher verschoben. */}
            <Header id="bank" icon={Landmark} title="Bankverbindung" hint="Auszahlungen aus Treuhand-Verkäufen"
                right={<BranchOverrideToggle area="bank" currentUser={currentUser}
                    infoText="Bankverbindung (IBAN, BIC, Kontoinhaber) des Hauptsitzes für alle Filialen übernehmen."
                    testId="bank-branch-override" />}
            />
            {open.bank && (
                <div className="p-5 border-b border-gray-200" data-testid="stammdaten-unternehmen-bank-body">
                    <BankSection currentUser={currentUser} embedded />
                </div>
            )}
            <Header id="stripeconnect" icon={CreditCard} title="Zahlungen empfangen (Stripe)" hint="Eigenes Stripe-Konto verbinden — Online-Zahlungen gehen direkt an Sie"
                right={<BranchOverrideToggle area="stripe" currentUser={currentUser}
                    infoText="Stripe-Konto des Hauptsitzes für alle Filialen übernehmen — Online-Zahlungen aller Filialen laufen dann über das zentrale Stripe-Konto."
                    testId="stripe-branch-override" />}
            />
            {open.stripeconnect && (
                <div className="p-5 border-b border-gray-200" data-testid="stammdaten-unternehmen-stripeconnect-body">
                    <StripeConnectCard />
                </div>
            )}
            <Header id="imprint" icon={ScrollText} title="Impressum" hint="Aktueller Impressums-Text — bearbeitbar"
                right={<BranchOverrideToggle
                    area="imprint"
                    currentUser={currentUser}
                    infoText="Das Impressum erscheint auf jedem Inserat (Verantwortlich für dieses Inserat) sowie auf Ihrer öffentlichen Händlerseite."
                    testId="imprint-branch-override"
                />}
            />
            {open.imprint && (
                <div className="p-5 border-b border-gray-200" data-testid="stammdaten-unternehmen-imprint-body">
                    <ImpressumSection currentUser={currentUser} embedded />
                </div>
            )}
            <Header id="legal" icon={FileSignature} title="Eigene Rechtsdokumente (PDF)" hint="Datenschutz, AGB (allgemein / Neufahrzeuge / Gebrauchte) & Kaufvertrag als PDF hinterlegen"
                right={<BranchOverrideToggle
                    area="legal_docs"
                    currentUser={currentUser}
                    infoText="Diese Rechtsdokumente werden bei der Vertragsabwicklung genutzt (z. B. ersetzt Ihr Kaufvertrag den automatisch erzeugten beim Sofortkauf) und auf Ihren öffentlichen Kundenseiten verlinkt."
                    subGroups={[
                        { key: "datenschutz", label: "Datenschutz" },
                        { key: "agb", label: "AGB (allgemein / Neu / Gebraucht)" },
                        { key: "widerruf", label: "Widerrufsbelehrung" },
                        { key: "kaufvertrag", label: "Kaufvertrag" },
                        { key: "impressum_link", label: "Impressum (Link/PDF)" },
                    ]}
                    testId="legal-docs-branch-override"
                />}
            />
            {open.legal && (
                <div className="p-5" data-testid="stammdaten-unternehmen-legal-body">
                    {/* Jun-2026 (User-Wunsch) — „Rechtliches" (AGB/Datenschutz/Spielregeln akzeptieren)
                        nach „Finanzen / Rechtliches" verschoben; die eigenen PDF-Dokumente bleiben hier.
                        Master-Switch steht jetzt rechts neben der Überschrift. */}
                    <div data-testid="stammdaten-unternehmen-legal-docs">
                        <h4 className="font-bold text-sm text-[#111827] mb-1">Eigene Rechtsdokumente & -texte</h4>
                        <p className="text-[11px] text-gray-500 mb-3">Datenschutz, AGB (allgemein / Neufahrzeuge / Gebrauchte), Kaufvertrag, Impressum & Widerruf — als Link oder PDF, mit „Gültig ab" &amp; Versionsverlauf.</p>
                        <OrgLockedSection area="legal_docs" label="Rechtsdokumente & -texte">
                            <LegalDocsUploader currentUser={currentUser} />
                            <div className="mt-5 pt-5 border-t border-gray-100">
                                <DealerLegalDocs currentUser={currentUser} />
                            </div>
                        </OrgLockedSection>
                    </div>
                </div>
            )}
        </div>
    );
}
